import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Users, Search, UploadCloud, Trophy, Flame, Bookmark, BookmarkCheck, Trash2, FileText, Loader2, ShieldCheck,
  Flag, ShieldOff, SlidersHorizontal, Lock, VolumeX, Eye,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssistantMessage } from "@/components/AssistantMessage";
import { ClassevivaImport } from "@/components/ClassevivaImport";
import { ClassroomImport } from "@/components/ClassroomImport";
import { supabase } from "@/integrations/supabase/client";
import {
  getMyCommunityProfile, updateCommunityProfile, searchStudents, toggleFollow, getChallengeBoard,
  searchSharedNotes, publishSharedNote, getSharedNote, toggleSaveSharedNote, deleteSharedNote,
  setUserBlock, listBlockedUsers, reportSharedNote, getMyReports,
} from "@/lib/community.functions";

export const Route = createFileRoute("/_app/community")({
  component: CommunityPage,
  head: () => ({
    meta: [
      { title: "Community studenti · Lumen" },
      { name: "description", content: "Segui i compagni, sfidatevi con XP e streak e cerca appunti scolastici condivisi per argomento." },
      { property: "og:title", content: "Community studenti · Lumen" },
      { property: "og:description", content: "Sfide di studio tra amici e libreria di appunti scolastici verificati." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

const LEVELS = ["elementari", "medie", "superiori", "universita"] as const;
const DOC_TYPES = ["notes", "summary", "exercises", "formulas", "slides", "past_paper"] as const;
const REPORT_REASONS = ["not_school", "spam_or_ads", "personal_data", "unsafe", "copyright", "low_quality"] as const;

function CommunityPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-5 px-5 pt-8">
      <PageHeader tone="sky" icon={<Users className="h-5 w-5" />} title={t("community.title")} subtitle={t("community.subtitle")} />
      <ProfileCard />
      <SyncCard />
      <Tabs defaultValue="challenge" className="space-y-4">
        <TabsList className="flex w-full gap-1 overflow-x-auto">
          <TabsTrigger value="challenge" className="min-w-0 flex-1 text-xs">{t("community.tabs.challenge")}</TabsTrigger>
          <TabsTrigger value="notes" className="min-w-0 flex-1 text-xs">{t("community.tabs.notes")}</TabsTrigger>
          <TabsTrigger value="upload" className="min-w-0 flex-1 text-xs">{t("community.tabs.upload")}</TabsTrigger>
          <TabsTrigger value="privacy" className="min-w-0 flex-1 text-xs">{t("community.tabs.privacy")}</TabsTrigger>
        </TabsList>
        <TabsContent value="challenge" className="space-y-4"><ChallengeTab /></TabsContent>
        <TabsContent value="notes"><NotesTab /></TabsContent>
        <TabsContent value="upload"><UploadTab /></TabsContent>
        <TabsContent value="privacy"><PrivacyTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------------- PROFILO ---------------------------------- */

function SyncCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["calendar_events"] });
    qc.invalidateQueries({ queryKey: ["homework"] });
  };
  return (
    <Card className="card-soft border-0 p-4">
      <div className="flex items-start gap-2">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0 space-y-2">
          <p className="text-sm font-medium">
            {t("community.sync.title", { defaultValue: "Account e compiti sincronizzati" })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("community.sync.subtitle", {
              defaultValue:
                "Accedi con Google o Apple per ritrovare appunti, XP e streak su ogni dispositivo. Collega il registro e Classroom per avere i compiti sempre aggiornati.",
            })}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {/* HIDDEN FOR V1 - APPLE 5.2.2 - No third-party credentials scraping */}
            {false && <ClassevivaImport onDone={invalidate} />}
            {false && <ClassevivaImport onDone={invalidate} provider="axios" />}
            <ClassroomImport source="community" onDone={invalidate} />
          </div>
        </div>
      </div>
    </Card>
  );
}

function ProfileCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const load = useServerFn(getMyCommunityProfile);
  const save = useServerFn(updateCommunityProfile);
  const { data } = useQuery({ queryKey: ["community", "me"], queryFn: () => load({ data: {} as never }) });
  const [username, setUsername] = useState<string | null>(null);
  const value = username ?? data?.profile?.username ?? "";

  const mutation = useMutation({
    mutationFn: (input: { username?: string; isPublic?: boolean }) => save({ data: input }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error === "username_taken" ? t("community.profile.taken") : t("states.error.title"));
      toast.success(t("community.profile.saved"));
      qc.invalidateQueries({ queryKey: ["community"] });
    },
  });

  const displayName = data?.profile?.username ?? data?.profile?.full_name ?? t("community.profile.title");

  return (
    <Card className="tone-sky overflow-hidden border-0 p-0">
      <div className="h-16 w-full" style={{ background: "linear-gradient(120deg, color-mix(in oklab, var(--sky) 80%, var(--card)), color-mix(in oklab, var(--lilac) 70%, var(--card)))" }} />
      <div className="-mt-8 space-y-3 p-4">
        <div className="flex items-end gap-3">
          <UserAvatar name={displayName} size={64} ring className="shadow-sm" />
          <div className="min-w-0 flex-1 pb-1">
            <h2 className="truncate text-base font-semibold leading-tight">{displayName}</h2>
            {data?.profile?.username && (
              <p className="truncate text-xs text-muted-foreground">@{data.profile.username}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 rounded-2xl bg-background/70 p-2 text-center">
          {[
            { v: data?.followers ?? 0, l: t("community.profile.followers") },
            { v: data?.following ?? 0, l: t("community.profile.following") },
            { v: data?.profile?.total_xp ?? 0, l: t("community.board.xp") },
            { v: data?.profile?.streak_days ?? 0, l: t("profile.streak") },
          ].map((s, i) => (
            <div key={i} className="min-w-0">
              <div className="text-sm font-bold leading-tight">{s.v}</div>
              <div className="truncate text-[11px] text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>

        <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
          <div className="space-y-1">
            <Label htmlFor="username" className="text-xs">{t("community.profile.username")}</Label>
            <Input id="username" value={value} placeholder={t("community.profile.usernamePlaceholder")} onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))} />
          </div>
          <Button
            disabled={mutation.isPending || value.trim().length < 3}
            onClick={() => mutation.mutate({ username: value.trim() })}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("community.profile.save")}
          </Button>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-2xl bg-background/60 p-3">
          <span className="text-xs">{t("community.profile.public")}</span>
          <Switch checked={data?.profile?.is_public ?? true} onCheckedChange={(v) => mutation.mutate({ isPublic: v })} />
        </div>
        {!data?.profile?.username && <p className="text-xs text-muted-foreground">{t("community.profile.needUsername")}</p>}
      </div>
    </Card>
  );
}


/* -------------------------------- SFIDA ----------------------------------- */

function ChallengeTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const board = useServerFn(getChallengeBoard);
  const find = useServerFn(searchStudents);
  const follow = useServerFn(toggleFollow);
  const [q, setQ] = useState("");

  const boardQuery = useQuery({ queryKey: ["community", "board"], queryFn: () => board({ data: {} as never }) });
  const peopleQuery = useQuery({ queryKey: ["community", "people", q], queryFn: () => find({ data: { q } }) });

  const followMutation = useMutation({
    mutationFn: (userId: string) => follow({ data: { userId } }),
    onSuccess: (res) => {
      if ("error" in res && res.error === "private") toast.error(t("community.privateProfile"));
      qc.invalidateQueries({ queryKey: ["community"] });
    },
  });

  return (
    <div className="space-y-4">
      <Card className="tone-peach space-y-3 p-4">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold"><Trophy className="h-4 w-4" /> {t("community.board.title")}</h2>
          <p className="text-xs text-muted-foreground">{t("community.board.subtitle")}</p>
        </div>
        <div className="space-y-2">
          {(boardQuery.data ?? []).map((p, i) => {
            const name = p.isMe ? t("community.board.you") : p.username ?? p.full_name ?? "—";
            const medal = ["🥇", "🥈", "🥉"][i];
            return (
              <div
                key={p.id}
                className={`flex items-center gap-3 rounded-2xl p-3 ${p.isMe ? "bg-background ring-1 ring-primary/30" : "bg-background/70"}`}
              >
                <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-bold">
                  {medal ?? i + 1}
                </span>
                <UserAvatar name={p.username ?? p.full_name ?? name} size={38} ring />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{name}</div>
                  <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                    <span>{t("community.board.level", { level: p.level ?? 1 })}</span>
                    <span className="inline-flex items-center gap-1"><Flame className="h-3 w-3" />{t("community.board.streak", { count: p.streak_days ?? 0 })}</span>
                    <span>{t("community.board.week", { count: p.weeklyMinutes })}</span>
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-primary/10 px-2 py-1 text-xs font-bold">
                  {p.total_xp ?? 0} {t("community.board.xp")}
                </span>
              </div>
            );
          })}

          {(boardQuery.data ?? []).length <= 1 && <p className="text-xs text-muted-foreground">{t("community.board.empty")}</p>}
        </div>
      </Card>

      <Card className="space-y-3 p-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("community.search.people")} className="pl-9" />
        </div>
        <div className="space-y-2">
          {(peopleQuery.data ?? []).map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-2xl bg-muted/40 p-3">
              <UserAvatar name={p.username ?? p.full_name} size={40} ring />
              <Link to="/student/$id" params={{ id: p.id }} className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{p.username ?? p.full_name}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {t("community.board.level", { level: p.level ?? 1 })} · {p.total_xp ?? 0} {t("community.board.xp")}
                </div>
              </Link>
              <Button size="sm" className="shrink-0 rounded-full" variant={p.following ? "secondary" : "default"} onClick={() => followMutation.mutate(p.id)}>
                {p.following ? t("community.unfollow") : t("community.follow")}
              </Button>
            </div>
          ))}

          {!peopleQuery.isLoading && !(peopleQuery.data ?? []).length && (
            <p className="text-xs text-muted-foreground">{t("community.search.empty")}</p>
          )}
        </div>
      </Card>
    </div>
  );
}

/* -------------------------------- APPUNTI --------------------------------- */

function NotesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const search = useServerFn(searchSharedNotes);
  const saveToggle = useServerFn(toggleSaveSharedNote);
  const remove = useServerFn(deleteSharedNote);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<"all" | "following" | "saved" | "mine">("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [subject, setSubject] = useState("");
  const [level, setLevel] = useState("any");
  const [docType, setDocType] = useState("any");
  const [sort, setSort] = useState<"recent" | "popular" | "saved">("recent");

  const list = useQuery({
    queryKey: ["community", "notes", scope, q, subject, level, docType, sort],
    queryFn: () =>
      search({
        data: {
          q, scope, sort,
          subject: subject.trim() || undefined,
          schoolLevel: level === "any" ? undefined : level,
          docType: docType === "any" ? undefined : docType,
        },
      }),
  });

  const saveMutation = useMutation({
    mutationFn: (id: string) => saveToggle({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["community", "notes"] }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => { toast.success(t("community.notes.deleted")); qc.invalidateQueries({ queryKey: ["community", "notes"] }); },
  });

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("community.search.notes")} className="pl-9" />
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {(["all", "following", "saved", "mine"] as const).map((s) => (
          <Button key={s} size="sm" variant={scope === s ? "default" : "outline"} className="shrink-0" onClick={() => setScope(s)}>
            {t(`community.notes.scope.${s}`)}
          </Button>
        ))}
        <Button size="sm" variant={showFilters ? "default" : "outline"} className="shrink-0" onClick={() => setShowFilters((v) => !v)}>
          <SlidersHorizontal className="mr-1 h-3.5 w-3.5" />{t("community.notes.filters")}
        </Button>
      </div>

      {showFilters && (
        <Card className="tone-sage grid gap-3 p-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">{t("community.upload.subject")}</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("community.notes.level")}</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("community.notes.any")}</SelectItem>
                {LEVELS.map((l) => <SelectItem key={l} value={l}>{t(`community.upload.levels.${l}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("community.notes.docType")}</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("community.notes.any")}</SelectItem>
                {DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{t(`community.notes.docTypes.${d}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("community.notes.sort")}</Label>
            <Select value={sort} onValueChange={(v) => setSort(v as typeof sort)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(["recent", "popular", "saved"] as const).map((s) => (
                  <SelectItem key={s} value={s}>{t(`community.notes.sorts.${s}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      <p className="text-xs text-muted-foreground">{t("community.notes.results", { count: (list.data ?? []).length })}</p>

      <div className="space-y-2">
        {(list.data ?? []).map((n) => (
          <Card key={n.id} className="card-soft space-y-3 border-0 p-4">
            {/* riga autore, stile post social */}
            <div className="flex items-center gap-2">
              <UserAvatar name={n.author} size={36} />
              <div className="min-w-0 flex-1">
                <Link to="/student/$id" params={{ id: n.authorId }} className="block truncate text-xs font-semibold hover:underline">
                  {n.author}
                </Link>
                <p className="truncate text-[11px] text-muted-foreground">
                  {n.subject} · {t(`community.notes.docTypes.${n.doc_type ?? "notes"}`)}
                </p>
              </div>
              {n.status !== "approved" && (
                <Badge variant="secondary" className="shrink-0 text-xs">{t(`community.notes.status.${n.status}`)}</Badge>
              )}
            </div>

            <button type="button" onClick={() => setOpenId(n.id)} disabled={n.status !== "approved" && !n.isMine} className="block w-full text-left">
              <h3 className="text-sm font-semibold leading-snug">{n.title}</h3>
              {n.description && <p className="mt-1 line-clamp-3 text-xs text-muted-foreground">{n.description}</p>}
            </button>

            {Boolean((n.topics ?? []).length) && (
              <div className="flex flex-wrap gap-1">
                {(n.topics ?? []).map((topic) => (
                  <button key={topic} onClick={() => setQ(topic)} className="rounded-full bg-primary/8 px-2 py-0.5 text-xs text-primary">#{topic}</button>
                ))}
              </div>
            )}

            <div className="flex items-center gap-1 border-t border-border/60 pt-2">
              <Button size="sm" variant="ghost" className="h-8 gap-1 px-2 text-xs" onClick={() => setOpenId(n.id)} disabled={n.status !== "approved" && !n.isMine}>
                <FileText className="h-3.5 w-3.5" />{t("community.notes.open")}
              </Button>
              <Button size="sm" variant="ghost" className="h-8 gap-1 px-2 text-xs" onClick={() => saveMutation.mutate(n.id)}>
                <Bookmark className="h-3.5 w-3.5" />{t("community.notes.save")}
              </Button>
              <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                <Eye className="h-3.5 w-3.5" />{n.views ?? 0}
              </span>
              {n.isMine ? (
                <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" onClick={() => deleteMutation.mutate(n.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ) : (
                <Button size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground" aria-label={t("community.report.action")} onClick={() => setReportId(n.id)}>
                  <Flag className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </Card>
        ))}

        {!list.isLoading && !(list.data ?? []).length && <p className="text-xs text-muted-foreground">{t("community.search.empty")}</p>}
      </div>

      <NoteDialog id={openId} onClose={() => setOpenId(null)} />
      <ReportDialog id={reportId} onClose={() => setReportId(null)} />
    </div>
  );
}

function NoteDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const get = useServerFn(getSharedNote);
  const saveToggle = useServerFn(toggleSaveSharedNote);
  const { data, isLoading } = useQuery({ queryKey: ["community", "note", id], queryFn: () => get({ data: { id: id! } }), enabled: Boolean(id) });
  const saveMutation = useMutation({
    mutationFn: () => saveToggle({ data: { id: id! } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["community"] }),
  });

  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader><DialogTitle className="pr-6 text-base">{data?.title ?? ""}</DialogTitle></DialogHeader>
        {isLoading && <Loader2 className="mx-auto h-5 w-5 animate-spin" />}
        {data && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">{data.subject} · {data.author}</p>
            {data.status !== "approved" && (
              <p className="rounded-xl bg-muted p-2 text-xs">{t(`community.notes.status.${data.status}`)} — {t("community.notes.rejectedHint")}</p>
            )}
            {data.fileUrl && (
              <a href={data.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-xs font-medium text-primary underline">
                <FileText className="h-4 w-4" />{t("community.notes.download")}
              </a>
            )}
            <AssistantMessage content={data.content} />
            <Button size="sm" variant="outline" onClick={() => saveMutation.mutate()}>
              {data.isSaved ? <BookmarkCheck className="mr-1 h-4 w-4" /> : <Bookmark className="mr-1 h-4 w-4" />}
              {data.isSaved ? t("community.notes.unsave") : t("community.notes.save")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------- CARICA ---------------------------------- */

function UploadTab() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const publish = useServerFn(publishSharedNote);
  const [form, setForm] = useState({ title: "", subject: "", topics: "", schoolLevel: "superiori", docType: "notes", downloadable: true, description: "", content: "" });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const lang = useMemo(() => (["it", "en", "es", "fr", "de", "pt"].includes(i18n.language) ? i18n.language : "it"), [i18n.language]);

  async function submit() {
    if (form.content.trim().length < 120) return toast.error(t("community.upload.contentPlaceholder"));
    setBusy(true);
    try {
      let filePath: string | undefined;
      let fileKind: string | undefined;
      if (file) {
        const { data: auth } = await supabase.auth.getUser();
        const uid = auth.user?.id;
        if (uid) {
          const path = `${uid}/${crypto.randomUUID()}-${file.name.replace(/[^\w.-]/g, "_")}`;
          const { error } = await supabase.storage.from("shared-notes").upload(path, file);
          if (error) throw new Error(error.message);
          filePath = path;
          fileKind = file.type;
        }
      }
      const res = await publish({
        data: {
          title: form.title.trim(),
          subject: form.subject.trim(),
          topics: form.topics.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 8),
          schoolLevel: form.schoolLevel as (typeof LEVELS)[number],
          docType: form.docType as (typeof DOC_TYPES)[number],
          downloadable: form.downloadable,
          language: lang as "it",
          description: form.description.trim() || undefined,
          content: form.content.trim(),
          filePath,
          fileKind,
        },
      });
      if (res.status === "approved") toast.success(t("community.upload.published"));
      else if (res.status === "pending") toast.info(t("community.upload.pending"));
      else toast.error(t("community.upload.rejected"));
      setForm({ ...form, title: "", subject: "", topics: "", description: "", content: "" });
      setFile(null);
      qc.invalidateQueries({ queryKey: ["community", "notes"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("states.error.title"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="tone-lilac space-y-3 p-4">
      <div>
        <h2 className="text-sm font-semibold">{t("community.upload.title")}</h2>
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />{t("community.upload.hint")}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label className="text-xs">{t("community.upload.noteTitle")}</Label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("community.upload.subject")}</Label>
          <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("community.upload.topics")}</Label>
          <Input value={form.topics} onChange={(e) => setForm({ ...form, topics: e.target.value })} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("community.upload.level")}</Label>
          <Select value={form.schoolLevel} onValueChange={(v) => setForm({ ...form, schoolLevel: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEVELS.map((l) => <SelectItem key={l} value={l}>{t(`community.upload.levels.${l}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">{t("community.notes.docType")}</Label>
          <Select value={form.docType} onValueChange={(v) => setForm({ ...form, docType: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{t(`community.notes.docTypes.${d}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex items-start justify-between gap-3 rounded-2xl bg-background/60 p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{t("community.privacy.allowDownloads")}</p>
        </div>
        <Switch checked={form.downloadable} onCheckedChange={(v) => setForm({ ...form, downloadable: v })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("community.upload.description")}</Label>
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("community.upload.content")}</Label>
        <Textarea rows={8} value={form.content} placeholder={t("community.upload.contentPlaceholder")} onChange={(e) => setForm({ ...form, content: e.target.value })} />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">{t("community.upload.fileLabel")}</Label>
        <Input type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      </div>
      <p className="text-xs text-muted-foreground">{t("community.upload.rules")}</p>
      <Button disabled={busy || form.title.length < 3 || form.subject.length < 2} onClick={submit} className="w-full">
        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("community.upload.publishing")}</> : <><UploadCloud className="mr-2 h-4 w-4" />{t("community.upload.submit")}</>}
      </Button>
    </Card>
  );
}

/* ------------------------- SEGNALAZIONI E PRIVACY -------------------------- */

function ReportDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const report = useServerFn(reportSharedNote);
  const [reason, setReason] = useState<(typeof REPORT_REASONS)[number]>("not_school");
  const [details, setDetails] = useState("");

  useEffect(() => { if (id) { setReason("not_school"); setDetails(""); } }, [id]);

  const send = useMutation({
    mutationFn: () => report({ data: { id: id!, reason, details: details.trim() || undefined } }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(t(`community.report.err.${res.error}`));
      toast.success(res.reviewed ? t("community.report.reviewed") : t("community.report.sent"));
      qc.invalidateQueries({ queryKey: ["community"] });
      onClose();
    },
  });

  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="text-base">{t("community.report.title")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t("community.report.subtitle")}</p>
          <div className="space-y-1">
            <Label className="text-xs">{t("community.report.reasonLabel")}</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as typeof reason)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((r) => <SelectItem key={r} value={r}>{t(`community.report.reasons.${r}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("community.report.details")}</Label>
            <Textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={3} maxLength={300} />
          </div>
          <Button className="w-full" disabled={send.isPending} onClick={() => send.mutate()}>
            {send.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Flag className="mr-1 h-4 w-4" />}
            {t("community.report.submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PrivacyTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const getProfile = useServerFn(getMyCommunityProfile);
  const update = useServerFn(updateCommunityProfile);
  const listBlocked = useServerFn(listBlockedUsers);
  const block = useServerFn(setUserBlock);
  const reports = useServerFn(getMyReports);

  const profile = useQuery({ queryKey: ["community", "me"], queryFn: () => getProfile({ data: undefined }) });
  const blocked = useQuery({ queryKey: ["community", "blocked"], queryFn: () => listBlocked({ data: undefined }) });
  const myReports = useQuery({ queryKey: ["community", "reports"], queryFn: () => reports({ data: undefined }) });

  const save = useMutation({
    mutationFn: (patch: Record<string, boolean>) => update({ data: patch }),
    onSuccess: () => { toast.success(t("community.privacy.savedOk")); qc.invalidateQueries({ queryKey: ["community"] }); },
  });
  const unblock = useMutation({
    mutationFn: (userId: string) => block({ data: { userId, kind: "none" } }),
    onSuccess: () => { toast.success(t("community.privacy.restored")); qc.invalidateQueries({ queryKey: ["community"] }); },
  });

  const p = profile.data?.profile;

  return (
    <div className="space-y-4">
      <Card className="tone-lilac space-y-4 p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold"><Lock className="h-4 w-4" />{t("community.privacy.title")}</h3>
        <ToggleRow
          label={t("community.privacy.discoverable")} hint={t("community.privacy.subtitle")}
          checked={p?.discoverable !== false} onChange={(v) => save.mutate({ discoverable: v })}
        />
        <ToggleRow
          label={t("community.privacy.showStats")} hint={""}
          checked={p?.show_stats !== false} onChange={(v) => save.mutate({ showStats: v })}
        />
        <ToggleRow
          label={t("community.privacy.allowDownloads")} hint={""}
          checked={p?.allow_downloads !== false} onChange={(v) => save.mutate({ allowDownloads: v })}
        />
      </Card>

      <Card className="space-y-2 p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold"><ShieldOff className="h-4 w-4" />{t("community.privacy.blockedTitle")}</h3>
        {!(blocked.data ?? []).length && <p className="text-xs text-muted-foreground">{t("community.privacy.blockedEmpty")}</p>}
        {(blocked.data ?? []).map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-2 rounded-2xl bg-muted/40 p-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{b.name}</p>
              <p className="text-xs text-muted-foreground">
                {b.kind === "block" ? t("community.privacy.block") : t("community.privacy.mute")}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => unblock.mutate(b.id)}>{t("community.privacy.remove")}</Button>
          </div>
        ))}
      </Card>

      <Card className="space-y-2 p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold"><Flag className="h-4 w-4" />{t("community.report.myTitle")}</h3>
        {!(myReports.data ?? []).length && <p className="text-xs text-muted-foreground">{t("community.report.myEmpty")}</p>}
        {(myReports.data ?? []).map((r) => (
          <div key={r.id} className="rounded-2xl bg-muted/40 p-3">
            <p className="truncate text-sm font-medium">{r.noteTitle}</p>
            <p className="text-xs text-muted-foreground">
              {t(`community.report.reasons.${r.reason}`)} · {t(`community.report.status.${r.status}`)}
            </p>
            {r.review_note && <p className="mt-1 text-xs text-muted-foreground">{r.review_note}</p>}
          </div>
        ))}
      </Card>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange }: { label: string; hint: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
